import { useState } from "react";
import { Link } from "react-router-dom";
import { useProfile } from "../../hooks/use-profile";
import { config } from "../../config";
import { ProfilePanel } from "../../components/layout/sidebar/profile-panel";
import type { UserProfile } from "../../components/layout/sidebar/types";

export function ProfileSettingsPage(): React.ReactElement {
  const { profile, isLoading, updateProfile } = useProfile(config.api.baseUrl);
  const [savingField, setSavingField] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const handleCreateProject = async (name: string) => {
    setSavingField("projects");
    setSaveError(null);
    try {
      const existing = profile?.projects ?? [];
      await updateProfile({
        projects: [...existing, { id: Date.now().toString(36), name }],
      });
    } catch (err) {
      setSaveError((err as Error).message);
    } finally {
      setSavingField(null);
    }
  };

  const handleUpdateField = async (field: string, value: string) => {
    setSavingField(field);
    setSaveError(null);

    try {
      // Build the update object from the dot-notation field path
      let updates: Partial<UserProfile>;

      if (field.startsWith("workingHours.")) {
        const subField = field.split(".")[1] as "start" | "end";
        updates = { workingHours: { ...(profile?.workingHours ?? { start: "", end: "" }), [subField]: value } };
      } else if (field.startsWith("notificationPreferences.")) {
        const subField = field.split(".")[1] as keyof NonNullable<UserProfile["notificationPreferences"]>;
        updates = {
          notificationPreferences: {
            ...(profile?.notificationPreferences ?? { dailyBriefing: false, overdueAlerts: false, reminderEmails: false }),
            [subField]: value === "true",
          },
        };
      } else {
        updates = { [field]: value } as Partial<UserProfile>;
      }

      await updateProfile(updates);
    } catch (err) {
      setSaveError((err as Error).message);
    } finally {
      setSavingField(null);
    }
  };

  return (
    <div className="px-4 sm:px-6 py-5 max-w-xl mx-auto">
      <div className="mb-5">
        <Link to="/" className="link text-xs">
          &larr; Home
        </Link>
      </div>

      <h1 className="text-lg font-semibold text-text-primary mb-4">Profile</h1>

      {saveError && (
        <div className="mb-4 p-3 bg-error/10 border border-error/20 rounded-lg text-sm text-error">
          Failed to save: {saveError}
        </div>
      )}

      <ProfilePanel
        profile={profile ?? null}
        onUpdateField={handleUpdateField}
        onCreateProject={handleCreateProject}
        isLoading={isLoading}
        savingField={savingField}
      />
    </div>
  );
}
